import styled, { css } from 'styled-components'

// Container for the entire section
export const Container = styled.div`
  margin: 0 auto;
  container-type: inline-size;
  container-name: geo-analysis;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

// Header section components removed - now using AnalysisSectionHeader from @popety-io/ui

// Grid layout for cards
/**
 * Tracks are minmax(0, 1fr) rather than 1fr: a bare `1fr` track floors at its
 * content's intrinsic width, and ECharts renders a child div with an explicit
 * pixel width. With plain `1fr` the chart cards refuse to shrink when the panel
 * narrows, overflowing the column instead.
 */
/**
 * The responsive rules only ever *reduce* the column count — they are skipped
 * for a single-column grid, which would otherwise be promoted to two and leave
 * its lone card filling half the width.
 */
const responsiveColumns = (columns: number) => css`
  @container geo-analysis (max-width: 900px) {
    grid-template-columns: repeat(${Math.min(columns, 2)}, minmax(0, 1fr));
  }

  @container geo-analysis (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }

  /* Fallback for browsers without container query support */
  @supports not (container-type: inline-size) {
    @media (max-width: 1024px) {
      grid-template-columns: repeat(${Math.min(columns, 2)}, minmax(0, 1fr));
    }
    @media (max-width: 768px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`

export const Grid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns || 3}, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
  overflow: visible;
  position: relative;
  z-index: 1;

  ${(props) => (props.$columns || 3) > 1 && responsiveColumns(props.$columns || 3)}
`

// Card component
export const Card = styled.div<{ $highlight?: boolean; $span?: number }>`
  border-radius: 10px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  grid-column: span ${(props) => props.$span || 1};
  position: relative;
  overflow: visible;
  /* Let the card shrink past its chart's intrinsic width (see Grid above). */
  min-width: 0;

  ${(props) =>
    props.$highlight &&
    `
    background: linear-gradient(135deg, #0294df 0%, #00B341 100%);
    color: white;
    border: none;

    & ${CardTitle}, & ${MetricLabel}, & ${MetricValue} {
      color: white;
    }
  `}
`

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

export const CardTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
`

// Score display component (keeping as requested)
export const ScoreCard = styled(Card)`
  background: linear-gradient(135deg, #00b341 0%, #0294df 100%);
  color: white;
  text-align: center;
`

export const ScoreValue = styled.div`
  font-size: 38px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 6px;
`

export const ScoreLabel = styled.div`
  font-size: 12px;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 1px;
`

// Metric display components
export const MetricGrid = styled.div`
  display: grid;
  gap: 12px;
  overflow: visible;
  position: relative;
`

export const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  overflow: visible;
  position: relative;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`

export const MetricLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
`

export const MetricValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const MetricHighlight = styled.span<{
  $type: 'success' | 'warning' | 'danger' | 'info'
  $textAlign?: 'left' | 'center' | 'right'
}>`
  font-size: 12px;
  font-weight: 600;
  text-align: ${(props) => (props.$textAlign ? props.$textAlign : 'left')};
  color: ${(props) =>
    props.$type === 'success'
      ? '#00B341'
      : props.$type === 'warning'
        ? '#FF9500'
        : props.$type === 'danger'
          ? '#FF3B30'
          : '#007AFF'};
`

// Icon container
export const IconContainer = styled.div<{ $color?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${(props) => props.$color || 'rgba(2, 148, 223, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

// Progress indicator
export const ProgressBar = styled.div`
  flex: 1;
  min-width: 0;
  height: 5px;
  border-radius: 3px;
  overflow: hidden;
  margin: 10px 0;
`

export const ProgressFill = styled.div<{
  $percentage: number
  $color?: string
}>`
  height: 100%;
  width: ${(props) => props.$percentage}%;
  background: ${(props) => props.$color || '#0294df'};
  border-radius: 3px;
  transition: width 0.3s ease;
`

// Tabs for different views
export const TabContainer = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
  padding: 3px;
  border-radius: 6px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`

export const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${(props) => (props.$active ? props.theme.colors.background.primary : 'transparent')};
  color: ${(props) =>
    props.$active ? props.theme.colors.text.primary : props.theme.colors.text.secondary};
  font-size: 12px;
  font-weight: ${(props) => (props.$active ? '600' : '500')};
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.background.primary};
  }
`

// Chart container
export const ChartContainer = styled.div`
  width: 100%;
  min-width: 0;
  height: 320px;
  position: relative;
  /* ECharts lags a frame behind a shrink; clip rather than push the layout. */
  overflow: hidden;
`

// Visualization container - for 3D charts
export const Visualization = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 6px;
  overflow: hidden;
`

// Boundary line style
export const Boundary = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: ${({ theme }) => theme.spacing.large} 0;
`
